import { useCallback, useState } from 'react'
import type { JSX } from 'react/jsx-runtime'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { supabase } from '@/lib/supabase'
import type {
  AttachmentRow,
  DocumentRow,
  ResearchProviderProps,
  SubmitResearchParams,
  UploadAttachment,
  UseResearchSimplifiedReturn,
} from '@/types'
import type { ResearchFormValues } from '@/lib/zod'
import { reportError } from '@/lib/error-utils'
import { researchContext } from './research-context'

export const ResearchProvider = ({
  children,
}: ResearchProviderProps): JSX.Element => {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  // UI State (not data)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [previewFile, setPreviewFile] = useState<AttachmentRow | undefined>()

  // Fetch research query
  const {
    data: research = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['research', user?.id],
    queryFn: async (): Promise<DocumentRow[]> => {
      if (!user) throw new Error('Authentication required')

      // Get user's professional profile with chamber
      const { data: professional, error: profError } = await supabase
        .from('professionals')
        .select('id, chamber_id')
        .eq('user_id', user.id)
        .single()

      if (profError) throw profError
      if (!professional.chamber_id) throw new Error('No chamber found')

      // Fetch documents with category='research'
      const { data, error } = await supabase
        .from('documents')
        .select(
          `
          *,
          attachments (*)
        `,
        )
        .eq('chamber_id', professional.chamber_id)
        .eq('category', 'research')
        .order('created_at', { ascending: false })

      if (error) throw error

      return data
    },
    enabled: Boolean(user),
  })

  // Filter research based on search and category (client-side)
  const getFilteredResearch = useCallback((): DocumentRow[] => {
    return research.filter((item) => {
      // Search filter
      const searchLower = searchQuery.toLowerCase()
      const isSearchMatch =
        item.title.toLowerCase().includes(searchLower) ||
        item.content?.toLowerCase().includes(searchLower) ||
        item.tags?.some((tag) => tag.toLowerCase().includes(searchLower))

      // Category filter
      let isCategoryMatch = true

      if (selectedCategory !== 'All') {
        if (selectedCategory === 'Files') {
          isCategoryMatch =
            Array.isArray(item.attachments) && item.attachments.length > 0
        } else if (selectedCategory === 'Notes') {
          isCategoryMatch =
            (!Array.isArray(item.attachments) ||
              item.attachments.length === 0) &&
            Boolean(item.content)
        }
      }

      return isSearchMatch && isCategoryMatch
    })
  }, [research, searchQuery, selectedCategory])

  // Submit research mutation
  const submitResearchMutation = useMutation({
    mutationFn: async ({ data, attachments }: SubmitResearchParams): Promise<void> => {
      if (!user) throw new Error('User not authenticated')

      // Get professional profile with chamber
      const { data: professional, error: profError } = await supabase
        .from('professionals')
        .select('id, chamber_id')
        .eq('user_id', user.id)
        .single()

      if (profError) throw profError
      if (!professional.chamber_id) throw new Error('No chamber found')

      // Process tags
      const tags = data.tags
        ? data.tags
            .split(',')
            .map((tag) => tag.trim())
            .filter((tag) => tag.length > 0)
        : null

      // Create research document entry
      const { data: newDocument, error: insertError } = await supabase
        .from('documents')
        .insert({
          title: data.title,
          content: data.content || null,
          category: 'research',
          document_type: 'other',
          tags,
          chamber_id: professional.chamber_id,
          user_id: user.id,
          file_url: '',
          file_size: 0,
          mime_type: 'application/octet-stream',
        })
        .select()
        .single()

      if (insertError) throw insertError

      // Upload attachments if any
      if (attachments.length > 0 && newDocument.id) {
        const bucketName = 'research-files'

        for (const attachment of attachments) {
          const filePath = `${user.id}/${newDocument.id}/${attachment.name}`

          // Upload file
          const { error: uploadError } = await supabase.storage
            .from(bucketName)
            .upload(filePath, attachment.file)

          if (uploadError) throw uploadError

          // Get public URL
          const { data: urlData } = supabase.storage
            .from(bucketName)
            .getPublicUrl(filePath)

          // Create attachment record
          await supabase.from('attachments').insert({
            name: attachment.name,
            document_id: newDocument.id,
            size: attachment.size,
            storage_path: filePath,
            type: attachment.type,
            url: urlData.publicUrl,
          })
        }
      }

      toast.success('Research document added successfully')
      setIsAddModalOpen(false)
    },
    onSuccess: () => {
      // Invalidate and refetch research
      void queryClient.invalidateQueries({ queryKey: ['research', user?.id] })
    },
    onError: (error) => {
      reportError({
        error,
        context: 'ResearchProvider.submitResearch',
        customMessage: 'Failed to add research document. Please try again.',
      })
    },
  })

  // Wrapper functions
  const submitResearch = useCallback(
    async (
      data: ResearchFormValues,
      attachments: UploadAttachment[],
    ): Promise<void> => {
      return submitResearchMutation.mutateAsync({ data, attachments })
    },
    [submitResearchMutation],
  )

  const refreshResearch = useCallback(async (): Promise<void> => {
    await refetch()
  }, [refetch])

  const value: UseResearchSimplifiedReturn = {
    // Data
    research,
    isLoading,

    // UI State
    searchQuery,
    selectedCategory,
    isAddModalOpen,
    previewFile,

    // Actions
    refreshResearch,
    setSearchQuery,
    setSelectedCategory,
    setIsAddModalOpen,
    setPreviewFile,
    submitResearch,
    getFilteredResearch,
  }

  return (
    <researchContext.Provider value={value}>
      {children}
    </researchContext.Provider>
  )
}
